// noseThreadsData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const noseThreads: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Nose Thread Lift | Elite Body Home Polyclinic",
            "description": "Nose threads, also known as a thread lift for the nose, is a popular non-surgical procedure designed to enhance the shape and contour of the nose without the need for invasive surgery.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/nose-threads/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Nose Thread Lift | Elite Body Home Polyclinic",
                "description": "Nose threads, also known as a thread lift for the nose, is a popular non-surgical procedure designed to enhance the shape and contour of the nose without the need for invasive surgery.",
                "url": "https://elitebodyhome.com/services/nose-threads/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:59:40+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/dfhgfhg.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/dfhgfhg.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Nose Threads",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Nose Thread Lift | Elite Body Home Polyclinic",
                "description": "Nose threads, also known as a thread lift for the nose, is a popular non-surgical procedure designed to enhance the shape and contour of the nose without the need for invasive surgery.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/dfhgfhg.jpg"
            },
        },
        "breadcrumbs": ["Home", "Services", "Nose Threads"],
        "hero": {
            "title": "Nose Threads",
            "subtitle": "Enhance Your Profile with Nose Threads",
            "description": "Nose threads, or thread lift for the nose is a minimally invasive, cosmetic procedure ideal for those looking to improve the shape and contour of the nose. This treatment provides a safe, effective, and relatively quick option for those who want to reshape their nose without the downtime and dangers of conventional rhinoplasty."
        },
        "howItWorks": {
            "title": "Procedure",
            "description": "Nose thread procedure is a non-surgical approach where biocompatible threads such as polydioxanone (PDO) threads are used in order to achieve lifting, shaping and contouring of the nose. Restructuring threads are laced under the skin to give our structure and support especially around the nasal bridge and tip."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "A comprehensive consultation is conducted to determine a patient's goals and whether they are a good candidate for the treatment.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Anesthesia",
                    "desc": "A topical anesthetic is given, so that the procedure is pain-free.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Thread Insertion",
                    "desc": "PDO threads are strategically placed underneath the skin of the nose using a fine needle, focusing on specific areas for the required lift and contouring. The threads dissolve gradually & over time, promote collagen formation as well as coveted internal scaffolding.",
                    "icon": "fa-solid fa-thread"
                },
                {
                    "title": "Shaping",
                    "desc": "Once in place, the threads are adjusted (stretched simply or extracted gently) to form the appealing contour: defined bridge, upturning tip, narrowed silhouette, and so forth.",
                    "icon": "fa-solid fa-ruler-combined"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "Read on to find out how nose threads can help improve your aesthetic appeal if you're looking for non-surgical treatment for the nose. It handles general cosmetic issues like flat or low nasal bridge, bulbous tip or slight asymmetry.",
            "items": [
                {
                    "title": "Non-surgical",
                    "desc": "No incision, less pain, and less downtime than traditional rhinoplasty.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "Immediate Results",
                    "desc": "Noticeable differences in the shape and contour of the nose can be observed immediately after the surgery.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Collagen Stimulation",
                    "desc": "PDO threads also promote collagen, improving the nose's structure and firmness overall.",
                    "icon": "fa-solid fa-seedling"
                },
                {
                    "title": "Customizable",
                    "desc": "The procedure can be customized for the patient's unique facial structure and goals.",
                    "icon": "fa-solid fa-sliders-h"
                },
                {
                    "title": "Natural-Looking Results",
                    "desc": "These threads create natural-looking improvements that highlight your best features.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Nose threads are a great alternative for anyone wanting to have slight changes in nose shape and structure. Although the treatment is generally safe and effective, it may not be appropriate for everyone.",
            "items": [
                {
                    "title": "Persons with low or flat nasal bridge",
                    "desc": "Nose threads are particularly popular in certain ethnicities, as they can be used to elevate and project the nasal bridge.",
                    "icon": "fa-solid fa-bridge"
                },
                {
                    "title": "People visiting for small changes",
                    "desc": "The procedure is most suitable to individuals who want subtle improvements rather than drastic alterations.",
                    "icon": "fa-solid fa-ruler-horizontal"
                },
                {
                    "title": "People who do not want to undergo surgery",
                    "desc": "If you want to improve the appearance of the nose without the downtime and risks associated with rhinoplasty, nose threads are a great alternative.",
                    "icon": "fa-solid fa-procedures"
                },
                {
                    "title": "Expecting realistic things from people",
                    "desc": "It's important to have a realistic expectation of the kind of results that can be the end product of non-surgical options.",
                    "icon": "fa-solid fa-lightbulb"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "A nose thread procedure can give the desired results, but only if you prepare yourself beforehand to avoid any complications.",
            "items": [
                {
                    "title": "Consultation & assessment",
                    "desc": "In the first consultation, discuss the aesthetic goals, medical history and discuss any possible concerns with the practitioner.",
                    "icon": "fa-solid fa-clipboard-question"
                },
                {
                    "title": "Avoid blood thinners",
                    "desc": "Avoid blood-thinning medications, supplements or substances (including aspirin, ibuprofen and alcohol) for at least a week before the procedure to minimize the risk of bruising.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "No makeup",
                    "desc": "Please come with face washed clear from make up's & lotions over the nose area on your treatment day.",
                    "icon": "fa-solid fa-paint-roller"
                },
                {
                    "title": "Skin care",
                    "desc": "Adhere to any instructions that your practitioner gives you to help care for your skin in the days following the procedure, like using gentle cleansers and avoiding difficult exfoliants.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "The effects of a nose thread are immediate and long-lasting.",
            "items": [
                {
                    "title": "Increased definition of the nasal bridge",
                    "desc": "Aesthetics of the nasal bridge are improved, both in height and structure, achieving a more refined appearance.",
                    "icon": "fa-solid fa-mountain"
                },
                {
                    "title": "Lifted nasal tip",
                    "desc": "These threads lift a drooping or rounded nasal tip creating a crisper, more balanced profile.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "Natural-looking contouring",
                    "desc": "Without appearing overdone or fake, the nose's overall shape is enhanced.",
                    "icon": "fa-solid fa-face-grin-stars"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long do nose threads last?",
                    "answer": "The effects of nose threads can last anywhere from 12 to 24 months, depending on the patient's skin type, lifestyle, and the specific PDO threads used. As the threads dissolve, collagen production continues, which helps maintain the enhanced shape over time."
                },
                {
                    "question": "Are nose threads painful?",
                    "answer": "Most patients report minimal discomfort during the procedure, thanks to the application of a local anesthetic. Some mild soreness, swelling, or bruising may occur afterward, but these side effects usually subside within a few days."
                },
                {
                    "question": "Can nose threads be combined with other treatments?",
                    "answer": "Yes, nose threads can be combined with other non-surgical treatments, such as dermal fillers, to further enhance the contour and structure of the nose. Consult with your practitioner to explore combination treatments that suit your goals."
                },
                {
                    "question": "How soon can I see results?",
                    "answer": "Results from nose threads are immediate, with the full effects becoming more apparent as any initial swelling subsides over the following days. The lift and contour will continue to improve as the threads stimulate collagen production."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/New-Service-Banner-Image-1-scaled.jpg",
            "beforeAfter": [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Nose-Threads-in-dubai-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Nose-Threads-in-dubai-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Nose-Threads-in-dubai-3.jpg"
            ],
            "introductionImage": ""
        },
        "product": {
            "id": 155,
            "title": "Nose Thread Lift",
            "price": 499,
            "description": "Non-surgical procedure to enhance and reshape the nose with thread lifting technology, offering subtle results without the need for surgery."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "فيلر الأنف في دبي | Elite Body Home Polyclinic",
            "description": "فيلر الأنف هو إجراء تجميلي غير جراحي مصمم لتحسين شكل وتناسق الأنف دون الحاجة إلى جراحة.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/nose-threads/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "فيلر الأنف في دبي | Elite Body Home Polyclinic",
                "description": "فيلر الأنف هو إجراء تجميلي غير جراحي مصمم لتحسين شكل وتناسق الأنف دون الحاجة إلى جراحة.",
                "url": "https://elitebodyhome.com/services/nose-threads/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:59:40+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/dfhgfhg.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/dfhgfhg.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "فيلر الأنف",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "فيلر الأنف في دبي | Elite Body Home Polyclinic",
                "description": "فيلر الأنف هو إجراء تجميلي غير جراحي مصمم لتحسين شكل وتناسق الأنف دون الحاجة إلى جراحة.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/dfhgfhg.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "فيلر الأنف"],
        "hero": {
            "title": "حسّن ملامح وجهك مع فيلر الأنف",
            "subtitle": "",
            "description": "احصل على ملامح الأنف التي تحلم بها دون الحاجة إلى الجراحة! توفر لك عيادات ايليت بودي هوم علاج فيلر الأنف بأحدث التقنيات. سواء كنت ترغب في التخلص من نتوءات الأنف، أو تحقيق توازن في شكل الأنف، أو رفع عظام الجسر، سيمنحك تجميل الأنف غير الجراحي لدينا نتائج سريعة وفعّالة مع فترة التعافي قصيرة أو دون الحاجة إليها. احصل على مظهر متناسق وطبيعي في أقل من ساعة، مع إجراء دقيق مخصص ليتناسب مع ملامح وجهك بأسلوب جمالي."
        },
        "howItWorks": {
            "title": "فيلر الأنف: نحت مثالي لملامح الأنف",
            "description": "يوفر فيلر الأنف طريقة متطورة للحصول على أنف أوروبية أكثر تحديدًا أو أنف ممدودة دون ألم وفي أقل من ساعة، مع نتائج فورية ودون الحاجة إلى فترة تعافي. يتم الإجراء في العيادة باستخدام فيلر جلدي عالي الجودة مكون من حمض الهيالورونيك، يُحقن في مناطق محددة من الأنف لتنعيم النتوءات، أو رفع الجسر المسطح، أو تصحيح عدم التماثل."
        },
        "procedureSteps": {
            "heading": "خطوات إجراء فيلر الأنف",
            "para": "",
            "steps": [
                {
                    "title": "الاستشارة",
                    "desc": "يقوم الطبيب بتقييم شكل الأنف ومناقشة التوقعات، وتحديد ما إذا كان الفيلر إجراءً ملائمًا لك أم لا بناءً على بنية الأنف وأهدافك.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "الاستعداد",
                    "desc": "يًنظف الأنف والمناطق المحيطة به بعناية، ثم يُستخدم كريم مخدر موضعي لتقليل الشعور بالألم.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "تحديد نقاط الحقن",
                    "desc": "يقوم الطبيب المعالج بوضع علامات دقيقة على مواقع الحقن لتشكيل الأنف وتحديده بالشكل المطلوب.",
                    "icon": "fa-solid fa-map-marker-alt"
                },
                {
                    "title": "حقن الفيلر",
                    "desc": "تُستخدم إبرة أو قنية دقيقة لحقن الفيلر في المناطق المستهدفة مثل جسر الأنف أو طرفه أو فتحات الأنف، حسب احتياجات المريض.",
                    "icon": "fa-solid fa-thread"
                },
                {
                    "title": "التشكيل والنحت",
                    "desc": "يقوم الطبيب المتخصص بتدليك الفيلر وتشكيله بلطف للحصول على تناسق مثالي وشكل متوازن.",
                    "icon": "fa-solid fa-ruler-combined"
                }
            ]
        },
        "benefits": {
            "heading": "مزايا فيلر الأنف",
            "subheading": "",
            "para": "يُعرف فيلر الأنف أيضًا بتجميل الأنف غير الجراحي، وقد اكتسب شعبية كبيرة بفضل قدرته على تحسين شكل الأنف وإعادة تشكيله دون الحاجة إلى الجراحة.",
            "items": [
                {
                    "title": "إجراء غير جراحي",
                    "desc": "لا يتطلب شقوق جراحية أو تخدير عام، مما يقلل من المخاطر وفترة التعافي.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "نتائج سريعة",
                    "desc": "يستغرق الإجراء أقل من 30 دقيقة، وتظهر النتائج على الفور.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "فترة تعافي قصيرة",
                    "desc": "يمكن للمرضى استئناف أنشطتهم اليومية مباشرةً بعد الجلسة مع فترة تعافي قصيرة أو دون الحاجة إليها.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "تعديلات مخصصة",
                    "desc": "يتيح الفيلر إجراء تغييرات دقيقة وطفيفة، مما يسمح بتعديل النتائج وفقًا لرغبة المريض.",
                    "icon": "fa-solid fa-sliders-h"
                },
                {
                    "title": "نتائج طبيعية",
                    "desc": "يمنح الفيلر تحسينات دقيقة تتناسب مع ملامح الوجه عند تطبيقه بشكل صحيح، مما يوفر مظهراً طبيعياً ومتناسقاً.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "eligibility": {
            "heading": "هل فيلر الأنف مناسب لك؟",
            "subheading": "",
            "para": "إذا كنت تبحث عن تعديلات غير جراحية لشكل الأنف، فقد يكون فيلر الأنف خيار مثالي لك. ورغم أن هذا الإجراء يوفر مرونة وراحة كبيرة، فمن المهم معرفة ما إذا كنت مرشحًا مناسبًا له.",
            "items": [
                {
                    "title": "تصحيح النتوءات الصغيرة",
                    "desc": "الأشخاص الذين يرغبون في تصحيح النتوءات أو المنخفضات الصغيرة في الأنف، أو عدم التماثل.",
                    "icon": "fa-solid fa-bridge"
                },
                {
                    "title": "حلول مؤقتة",
                    "desc": "يوفر الفيلر خيارًا مؤقت لإعادة تشكيل الأنف، مما يجعله مثاليًا لمن يرغب في تجربة التغيير قبل اتخاذ قرار دائم.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "تجنب العمليات الجراحية",
                    "desc": "يعد الفيلر خيارًا آمنًا وسريعًا لمن يبحث عن بديل غير جراحي لعملية تجميل الأنف.",
                    "icon": "fa-solid fa-procedures"
                },
                {
                    "title": "تحسينات طبيعية",
                    "desc": "يناسب من يرغب في إجراء تعديل بسيط يعزز تناسق ملامح الوجه دون تغيير جذري في بنيته.",
                    "icon": "fa-solid fa-lightbulb"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "يعتبر فيلر الأنف إجراء غير جراحي، ولكن يوجد بعض النصائح الهامة التي يجب اتباعها قبل موعد جلستك، ويمكن أن تُحدث فارقًا كبيرًا في تجربتك بشكل عام.",
            "items": [
                {
                    "title": "تجنب مميعات الدم",
                    "desc": "يجب تجنب تناول الأسبرين والإيبوبروفين وأي أدوية مميعة للدم لمدة أسبوع على الأقل قبل إجراء الحقن.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "الامتناع عن الكحول والتدخين",
                    "desc": "يمكن أن يبطئ الكحول والتدخين من عملية الشفاء ويرفعان من فرصة التورم. يُفضل الابتعاد عنهم لمدة 48 ساعة على الأقل قبل العلاج.",
                    "icon": "fa-solid fa-smoking-ban"
                },
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "يساعد شرب كمية كافية من الماء في الحفاظ على ترطيب الجسم، مما يساعد الفيلر على العمل بشكل أفضل ويعزز الشفاء بسرعة أكبر.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "استشارة الطبيب",
                    "desc": "أخبر الطبيب المعالج إذا كنت تعاني من حساسية أو مشكلات طبية سابقة أو إذا كنت تتناول أي أدوية.",
                    "icon": "fa-solid fa-user-md"
                }
            ]
        },
        "results": {
            "heading": "نتائج فيلر الانف",
            "subheading": "",
            "para": "يعد فيلر الأنف خيار غير جراحي مذهل لتحسين ميلان الأنف وزيادة رضا المرضى، حيث يوفر نتائج فورية ووقت تعافي قصير.",
            "items": [
                {
                    "title": "نتائج فورية",
                    "desc": "ستلاحظ تحسنًا واضحًا بعد الإجراء مباشرةً، حيث سيبدو أنفك أكثر تناسقًا وتحديدًا.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "تحسينات طبيعية",
                    "desc": "يمنحك الفيلر مظهرًا متناسقًا دون مبالغة، مما يعزز شكل الأنف بشكل طبيعي.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "نتائج مؤقتة",
                    "desc": "تدوم النتائج عادةً ما بين 6 إلى 18 شهرًا، حسب نوع الفيلر المستخدم.",
                    "icon": "fa-solid fa-calendar-alt"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح العناية بعد فيلر الأنف",
            "subheading": "",
            "para": "تُعدّ العناية بعد العلاج أمرًا ضروريًا للحصول على أفضل النتائج وتقليل الآثار الجانبية.",
            "items": [
                {
                    "title": "تجنب لمس الأنف",
                    "desc": "لا تلمس أو تدلك أو تضغط على الأنف لمدة 24-48 ساعة على الأقل بعد العلاج.",
                    "icon": "fa-solid fa-hand-paper"
                },
                {
                    "title": "تجنب الشمس",
                    "desc": "احرص على وضع واقي الشمس وتجنب أشعة الشمس المباشرة لبضعة أيام بعد العلاج.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "تجنب التمارين الشاقة",
                    "desc": "يُفضَّل الامتناع عن التمارين الرياضية الشاقة أو التعرق المفرط لمدة يوم أو يومين.",
                    "icon": "fa-solid fa-running"
                },
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "يساعد شرب كميات كافية من الماء على ترطيب البشرة وتعزيز عملية التعافي.",
                    "icon": "fa-solid fa-tint"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "كم تدوم نتائج فيلر الأنف؟",
                    "answer": "تدوم النتائج عادةً ما بين 6 إلى 18 شهرًا، حسب نوع الفيلر المستخدم وطبيعة جلد المريض."
                },
                {
                    "question": "هل فيلر الأنف مؤلم؟",
                    "answer": "يتم استخدام مخدر موضعي لجعل الإجراء مريحًا قدر الإمكان. قد يشعر المريض بوخز طفيف أثناء الحقن."
                },
                {
                    "question": "هل يمكن الجمع بين فيلر الأنف وعلاجات أخرى؟",
                    "answer": "نعم، يمكن الجمع بين فيلر الأنف وعلاجات تجميلية أخرى مثل البوتوكس أو الفيلر في مناطق أخرى من الوجه بعد استشارة الطبيب."
                },
                {
                    "question": "متى يمكن رؤية النتائج النهائية؟",
                    "answer": "تظهر النتائج الأولية فورًا بعد الإجراء، بينما تظهر النتائج النهائية بعد زوال أي تورم طفيف خلال أيام قليلة."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }
};
